import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Save, Plus, Trash2, ExternalLink, FileText, Globe, Upload, File } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { contentService, adminService } from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminResources = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const { toast } = useToast();
  const fileInputRefs = useRef({});
  
  const [resources, setResources] = useState([
    { id: '1', title: '香港法定假日', titleEn: 'Hong Kong Public Holidays', description: '2026年香港法定假日一覽', descriptionEn: '2026 Hong Kong Public Holidays List', link: 'https://www.labour.gov.hk/tc/news/latest_holidays2026.htm', type: 'link' },
    { id: '2', title: '聘用家庭傭工的標準僱傭合約 (英文)', titleEn: 'Standard Employment Contract (English)', description: 'ID407 Standard Employment Contract (English)', descriptionEn: 'ID407 Standard Employment Contract for Foreign Domestic Helper', link: 'https://www.immd.gov.hk/eng/forms/forms/id407.html', type: 'link' },
    { id: '3', title: '聘用家庭傭工的標準僱傭合約 (中文)', titleEn: 'Standard Employment Contract (Chinese)', description: 'ID407 標準僱傭合約 (中文版)', descriptionEn: 'ID407 Standard Employment Contract (Chinese Version)', link: 'https://www.immd.gov.hk/hkt/forms/forms/id407f.html', type: 'link' }
  ]);

  const [usefulLinks, setUsefulLinks] = useState([
    { id: '1', title: '勞工處', titleEn: 'Labour Department', description: '香港勞工處官方網站', descriptionEn: 'Hong Kong Labour Department Official Website', link: 'https://www.labour.gov.hk/tc/index.htm' },
    { id: '2', title: '入境事務處', titleEn: 'Immigration Department', description: '香港入境事務處官方網站', descriptionEn: 'Hong Kong Immigration Department Official Website', link: 'https://www.immd.gov.hk/hkt/index.html' }
  ]);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      if (response.data?.resources?.length > 0) {
        setResources(response.data.resources);
      }
      if (response.data?.usefulLinks?.length > 0) {
        setUsefulLinks(response.data.usefulLinks);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentContent = await contentService.get();
      const updatedContent = {
        ...currentContent.data,
        resources: resources,
        usefulLinks: usefulLinks
      };
      
      await adminService.content.update(updatedContent);
      
      toast({
        title: '成功 / Success',
        description: '有用資料已保存 / Resources saved'
      });
    } catch (error) {
      console.error('Error saving resources:', error);
      toast({
        title: '錯誤 / Error',
        description: '保存失敗，請再試一次 / Save failed, please try again',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|xls|xlsx)$/i)) {
      toast({
        title: '錯誤',
        description: '只支援 PDF, DOC, DOCX, XLS, XLSX 檔案',
        variant: 'destructive'
      });
      return;
    }
    
    setUploading(index);
    
    try {
      const response = await adminService.upload.uploadFile(file);
      
      if (response.data.success) {
        const newResources = [...resources];
        newResources[index] = {
          ...newResources[index],
          link: `${BACKEND_URL}${response.data.url}`,
          type: 'file',
          filename: response.data.filename,
          originalFilename: response.data.original_filename
        };
        setResources(newResources);
        
        toast({
          title: '成功',
          description: '檔案已上傳'
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: '錯誤',
        description: '上傳失敗，請再試一次',
        variant: 'destructive'
      });
    } finally {
      setUploading(null);
    }
  };

  const addResource = () => {
    const newResource = {
      id: Date.now().toString(),
      title: '',
      titleEn: '',
      description: '',
      descriptionEn: '',
      link: '',
      type: 'link'
    };
    setResources([...resources, newResource]);
  };

  const removeResource = (index) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  const updateResource = (index, field, value) => {
    const newResources = [...resources];
    newResources[index][field] = value;
    if (field === 'link' && !value.includes('/api/uploads/')) {
      newResources[index].type = 'link';
    }
    setResources(newResources);
  };

  const addUsefulLink = () => {
    const newLink = {
      id: Date.now().toString(),
      title: '',
      titleEn: '',
      description: '',
      descriptionEn: '',
      link: ''
    };
    setUsefulLinks([...usefulLinks, newLink]);
  };

  const removeUsefulLink = (index) => {
    setUsefulLinks(usefulLinks.filter((_, i) => i !== index));
  };

  const updateUsefulLink = (index, field, value) => {
    const newLinks = [...usefulLinks];
    newLinks[index][field] = value;
    setUsefulLinks(newLinks);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>有用資料管理 / Resources Management</h1>
        <p className="text-gray-600 mt-2">管理常用表格、文件及網站連結（中英文）/ Manage forms, documents and links (Chinese & English)</p>
      </div>

      {/* Documents and Forms Section */}
      <Card className="mb-8">
        <CardHeader style={{ backgroundColor: '#f8fffe' }}>
          <div className="flex justify-between items-center">
            <CardTitle style={{ color: '#00b6a5' }}>
              <FileText className="inline-block mr-2 h-5 w-5" />
              常用表格及文件 / Forms & Documents
            </CardTitle>
            <Button onClick={addResource} size="sm" style={{ backgroundColor: '#00b6a5' }}>
              <Plus className="h-4 w-4 mr-1" />
              新增
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {resources.map((resource, index) => (
              <div key={resource.id} className="p-4 border rounded-lg bg-gray-50 space-y-4">
                {/* Chinese fields */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium mb-1">標題（中文）*</label>
                    <Input
                      value={resource.title}
                      onChange={(e) => updateResource(index, 'title', e.target.value)}
                      placeholder="例：香港法定假日"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium mb-1">描述（中文）</label>
                    <Input
                      value={resource.description}
                      onChange={(e) => updateResource(index, 'description', e.target.value)}
                      placeholder="例：2025年香港法定假日一覽"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-sm font-medium mb-1">
                      連結 / 檔案 *
                      {resource.type === 'file' && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                          已上傳檔案
                        </span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={resource.link}
                        onChange={(e) => updateResource(index, 'link', e.target.value)}
                        placeholder="https://... 或上傳檔案"
                        className="flex-1"
                      />
                    </div>
                    {resource.originalFilename && (
                      <p className="text-xs text-gray-500 mt-1">
                        <File className="inline h-3 w-3 mr-1" />
                        {resource.originalFilename}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">操作</label>
                    <div className="flex gap-1">
                      <input
                        type="file"
                        ref={(el) => (fileInputRefs.current[index] = el)}
                        onChange={(e) => handleFileUpload(index, e.target.files[0])}
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        style={{ display: 'none' }}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => fileInputRefs.current[index]?.click()}
                        disabled={uploading === index}
                        title="上傳檔案"
                      >
                        {uploading === index ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                      </Button>
                      {resource.link && (
                        <a href={resource.link} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="icon" title="開啟連結">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => removeResource(index)}
                        title="刪除"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* English fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-blue-700">Title (English)</label>
                    <Input
                      value={resource.titleEn || ''}
                      onChange={(e) => updateResource(index, 'titleEn', e.target.value)}
                      placeholder="e.g., Hong Kong Public Holidays"
                      className="border-blue-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-blue-700">Description (English)</label>
                    <Input
                      value={resource.descriptionEn || ''}
                      onChange={(e) => updateResource(index, 'descriptionEn', e.target.value)}
                      placeholder="e.g., 2025 Hong Kong Public Holidays List"
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </div>
            ))}
            {resources.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                尚未新增任何表格或文件
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Useful Links Section */}
      <Card className="mb-8">
        <CardHeader style={{ backgroundColor: '#f8fffe' }}>
          <div className="flex justify-between items-center">
            <CardTitle style={{ color: '#00b6a5' }}>
              <Globe className="inline-block mr-2 h-5 w-5" />
              常用網站連結 / Useful Links
            </CardTitle>
            <Button onClick={addUsefulLink} size="sm" style={{ backgroundColor: '#00b6a5' }}>
              <Plus className="h-4 w-4 mr-1" />
              新增
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {usefulLinks.map((link, index) => (
              <div key={link.id} className="p-4 border rounded-lg bg-gray-50 space-y-4">
                {/* Chinese fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">標題（中文）*</label>
                    <Input
                      value={link.title}
                      onChange={(e) => updateUsefulLink(index, 'title', e.target.value)}
                      placeholder="例：勞工處"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">描述（中文）</label>
                    <Input
                      value={link.description}
                      onChange={(e) => updateUsefulLink(index, 'description', e.target.value)}
                      placeholder="例：香港勞工處官方網站"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">連結 *</label>
                    <div className="flex gap-2">
                      <Input
                        value={link.link}
                        onChange={(e) => updateUsefulLink(index, 'link', e.target.value)}
                        placeholder="https://..."
                        className="flex-1"
                      />
                      {link.link && (
                        <a href={link.link} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="icon">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => removeUsefulLink(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* English fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-blue-700">Title (English)</label>
                    <Input
                      value={link.titleEn || ''}
                      onChange={(e) => updateUsefulLink(index, 'titleEn', e.target.value)}
                      placeholder="e.g., Labour Department"
                      className="border-blue-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-blue-700">Description (English)</label>
                    <Input
                      value={link.descriptionEn || ''}
                      onChange={(e) => updateUsefulLink(index, 'descriptionEn', e.target.value)}
                      placeholder="e.g., Hong Kong Labour Department Official Website"
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </div>
            ))}
            {usefulLinks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                尚未新增任何網站連結
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pb-24">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          style={{ backgroundColor: '#00b6a5' }}
          className="text-white"
        >
          {saving ? (
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
          ) : (
            <Save className="h-5 w-5 mr-2" />
          )}
          保存所有更改 / Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminResources;
